"use client";

import ProductPage from "@/components/ProductPage";
import ImageHero from "./hero.jpg";
import ImageHighlight from "./highlight.jpg";
import ImageFeaturePOS from "./feature-pos.jpg";
import ImageFeatureCustomers from "./feature-customers.jpg";
import ImageFeatureStock from "./feature-stock.jpg";
import ImageFeatureStats from "./feature-stats.jpg";
import ImageFeatureOrder from "./feature-order.jpg";
import ImageFeatureExport from "./feature-export.jpg";
import { Check } from "lucide-react";
import ImageText from "@/components/ImageText";
import Section from "@/components/Section";
import SpecialFeaturesSection from "@/components/SpecialFeaturesSection";
import Image from "next/image";

export default function ModeProfi() {
  return (
    <ProductPage
      productName="ModeProfi – Warenwirtschaftssystem für den Textilgroßhandel"
      hero={{
        title: "ModeProfi – Die Kassenlösung für den Textilhandel",
        subtitle:
          "Speziell entwickelt für Mode, Textil und Accessoires: Vollständige Kassenlösung mit Online-Funktionalität, EDI-Schnittstelle und direkter Steuerberater-Anbindung – kinderleicht zu bedienen, auf Zukunft programmiert.",
        buttonLeft: {
          title: "Jetzt unverbindlich anfragen",
          href: "/support",
        },
        buttonRight: { title: "Preise ansehen", href: "/preise" },
        image: ImageHero,
        imageAlt: "ModeProfi Kassensystem für den Textilhandel",
      }}
      highlight={{
        image: ImageHighlight,
        bullets: [
          <>
            <strong>100 % rechtssicher</strong> –{" "}
            <a
              href="https://www.bzst.de/DE/Unternehmen/Aussenpruefungen/DigitaleSchnittstelleFinV/digitaleschnittstellefinv_node.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-primary underline hover:opacity-80"
            >
              DSFinVK
            </a>
            -,{" "}
            <a
              href="https://www.bsi.bund.de/DE/Themen/Unternehmen-und-Organisationen/Standards-und-Zertifizierung/Schutz-vor-Manipulation-an-digitalen-Grundaufzeichnungen/Fragen-und-Antworten/fragen-und-antworten_node.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-primary underline hover:opacity-80"
            >
              TSE
            </a>
            - und{" "}
            <a
              href="https://www.ris.bka.gv.at/GeltendeFassung.wxe?Abfrage=Bundesnormen&Gesetzesnummer=20009390"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-primary underline hover:opacity-80"
            >
              RKSV
            </a>
            -konform,{" "}
            <a
              href="https://www.hsp-steuer.de/2017/01/18/gobs-gobd-gdpdu-einfach-erklaert/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-primary underline hover:opacity-80"
            >
              GoBD
            </a>
            /
            <a
              href="https://www.hsp-steuer.de/2017/01/18/gobs-gobd-gdpdu-einfach-erklaert/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-primary underline hover:opacity-80"
            >
              GDPdU
            </a>
            -konform, mit direkter Steuerberater-Anbindung und{" "}
            <a
              href="https://www.softguide.de/funktion/datev-export"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-primary underline hover:opacity-80"
            >
              DATEV
            </a>
            -Export.
          </>,
          <>
            <strong>Volle Vernetzung im Filialbetrieb</strong> – alle Vorgänge
            sind online sofort an allen Kassen verfügbar, zentrale
            Administration mit Funktionsüberwachung inklusive.
          </>,
          <>
            <strong>Gemacht für den Textilhandel</strong> – Größen- und
            Farbmatrix, Saisonverwaltung, Atelierverwaltung,{" "}
            <a
              href="https://www.seeburger.com/de/ressourcen/wissenswertes/was-ist-edi"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-primary underline hover:opacity-80"
            >
              EDI
            </a>
            -Schnittstelle und nahtlose Schnittstellen zu Online-Shops und
            Buchhaltung.
          </>,
          <span>
            <strong>Finanzamt- und Gesetzkonform</strong>
          </span>,
        ],
      }}
      beforeVideo={
        <>
          <Section className="bg-white py-4 md:py-6 mt-12">
            <ImageText
              imagePosition="right"
              imageSlot={
                <div className="relative overflow-hidden mt-12 rounded-2xl border border-black/10 bg-black shadow-sm h-auto md:h-100">
                  <video
                    className="w-full h-auto md:h-full md:object-cover"
                    controls
                  >
                    <source src="/VideoFürWebseite.mp4" type="video/mp4" />
                  </video>
                </div>
              }
            >
              <div className="flex flex-col gap-2 mb-2">
                <Image
                  src="/modeprofi.svg"
                  alt="ModeProfi"
                  width={120}
                  height={36}
                  className="object-contain"
                />
                <h2 className="text-3xl md:text-4xl font-semibold text-[#050a18]">
                  Die Profi-Kasse für Mode und Textil
                </h2>
              </div>

              <p className="text-slate-700 leading-relaxed">
                ModeProfi wurde von Grund auf für den Textilhandel entwickelt –
                mit allen Funktionen die Sie im täglichen Betrieb wirklich
                brauchen. Kinderleicht zu bedienen, offiziell freigegeben für
                Windows 10 & 11 und sofort einsatzbereit dank schneller
                Installationsroutine.
              </p>

              <p className="mt-4 text-slate-700 leading-relaxed">
                Ob Einzelfiliale oder Filialkette – alle Vorgänge sind online
                sofort an allen Kassen verfügbar. Mit EDI-Schnittstelle,
                DATEV-Export und direkter Anbindung an Ihren Steuerberater
                laufen Ihre Prozesse ohne Medienbrüche.
              </p>

              <ul className="mt-4 space-y-2">
                <li className="flex items-start gap-3 text-slate-700">
                  <Check
                    className="shrink-0 mt-1 text-brand-primary"
                    size={18}
                  />
                  <span>
                    <strong>Textile Stärke</strong> – Größen- und Farbmatrix,
                    Saisonverwaltung, Atelierverwaltung und Auswahlverwaltung
                    direkt im System
                  </span>
                </li>
                <li className="flex items-start gap-3 text-slate-700">
                  <Check
                    className="shrink-0 mt-1 text-brand-primary"
                    size={18}
                  />
                  <span>
                    <strong>Vollständige Kassenfunktionen</strong> – Barzahlung,
                    Kartenzahlung, Gutscheine, GiftCards, Kundenretouren und
                    Bon-Parken
                  </span>
                </li>
                <li className="flex items-start gap-3 text-slate-700">
                  <Check
                    className="shrink-0 mt-1 text-brand-primary"
                    size={18}
                  />
                  <span>
                    <strong>Nahtlose Integration</strong> – EDI-Schnittstelle,
                    DATEV-Export und direkte Schnittstelle zu Ihrem
                    Steuerberater ohne zusätzlichen Aufwand
                  </span>
                </li>
              </ul>
            </ImageText>
          </Section>

          <SpecialFeaturesSection
            industries={[
              {
                title: "Textilhandel & Mode",
                standard: [
                  "Rechnungen & Angebote",
                  "Kundenverwaltung",
                  "Artikelstammdaten",
                  "Reporting & Berichte",
                  "Lieferantenverwaltung",
                  "Zahlungsverwaltung",
                  "Beleghistorie",
                  "Mahnwesen",
                ],
                special: [
                  "Größen- und Farbmatrix für Textil, Schuhe und Accessoires",
                  "Saisonverwaltung & Kollektionsverwaltung",
                  "Atelierverwaltung & Auswahlverwaltung",
                  "EDI-Schnittstelle & Schnittstelle zum Steuerberater",
                  "DATEV-Export & GoBD/GDPdU-konform",
                  "DSFinVK-, TSE- und RKSV-konform",
                  "Zentrale Administration mit Funktionsüberwachung im Filialbetrieb",
                  "Alle Vorgänge online sofort an allen Kassen verfügbar",
                ],
              },
            ]}
          />
        </>
      }
      faqs={[
        {
          q: "Ist ModeProfi speziell für den Textilhandel entwickelt?",
          a: "Ja, ModeProfi wurde gezielt für Mode-, Textil- und Accessoires-Unternehmen entwickelt – mit Größen- und Farbmatrix, Saisonverwaltung, Atelierverwaltung und allen typischen Abläufen im Textilhandel.",
        },
        {
          q: "Welche Schnittstellen bietet ModeProfi?",
          a: "ModeProfi bietet eine EDI-Schnittstelle, DATEV-Export sowie eine direkte Schnittstelle zu Ihrem Steuerberater – alle Daten fließen ohne Medienbrüche in Ihre Buchhaltung.",
        },
        {
          q: "Ist ModeProfi rechtssicher und konform?",
          a: "Ja, ModeProfi ist DSFinVK-, TSE- und RKSV-konform sowie GoBD/GDPdU-konform – Sie sind jederzeit auf der sicheren Seite gegenüber Finanzamt und Betriebsprüfung.",
        },
        {
          q: "Kann ModeProfi im Filialbetrieb eingesetzt werden?",
          a: "Ja, ModeProfi ermöglicht eine zentrale Administration mit Funktionsüberwachung – alle Vorgänge sind online sofort an allen Kassen verfügbar, egal wie viele Filialen Sie betreiben.",
        },
        {
          q: "Welche Kassenfunktionen sind enthalten?",
          a: "Enthalten sind Barzahlung, Kartenzahlung mit Zahlungsterminal-Anbindung, Gutscheine, GiftCards, Reservierungen, Kundenretouren, Bon-Parken, Tagesabschluss, BonJournal und Bonnachdruck.",
        },
        {
          q: "Für welche Betriebssysteme ist ModeProfi freigegeben?",
          a: "ModeProfi ist offiziell für Windows 10 und Windows 11 freigegeben und lässt sich dank schneller Installationsroutine sofort in Betrieb nehmen.",
        },
      ]}
      features={[
        {
          src: ImageFeaturePOS,
          alt: "Kasse & Verkauf",
          title: "Kasse & Verkauf",
          desc: "Barzahlung, Karte, Gutscheine & mehr",
        },
        {
          src: ImageFeatureCustomers,
          alt: "Kundenverwaltung",
          title: "Kundenverwaltung",
          desc: "Historie, Bonus & Dashboard",
        },
        {
          src: ImageFeatureStock,
          alt: "Textil & Lager",
          title: "Textil & Lager",
          desc: "Größen, Farben & Saisons",
        },
        {
          src: ImageFeatureStats,
          alt: "Berichte",
          title: "Berichte",
          desc: "Tagesabschluss & Auswertungen",
        },
        {
          src: ImageFeatureOrder,
          alt: "Reservierungen",
          title: "Reservierungen",
          desc: "Aufträge & Retouren",
        },
        {
          src: ImageFeatureExport,
          alt: "Schnittstellen",
          title: "Schnittstellen",
          desc: "EDI, DATEV & Steuerberater",
        },
      ]}
    />
  );
}
